import "@toast-ui/editor/dist/toastui-editor.css";
import { Viewer } from "@toast-ui/react-editor";
import moment from "moment";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { ContractApplicationWrap } from "src/api/contract/contract-types";
import noImage from "src/assets/images/noImage_default.svg";
import { ResizedImage } from "src/components";
import ContractFileUpload from "src/components/ContractFileUpload";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { getMypageTitle } from "src/utils/app-Info";
import { useNavigateGoBack } from "src/pages/hooks/navigate-go-back";
import { useMemberInfoState } from "src/recoil/member/hook";
import { formatDateTimeRange, formatPhoneNumber, getUseTimeDiff, ViewYmdFormat } from "src/utils/common-util";

// 색상 / 띄어쓰기 / 이용신청서 사용 통과

const ContractApplicationDetail = () => {
  const { t, i18n } = useTranslation("contract");
  const isEnglish = i18n.language?.startsWith("en");
  const { state } = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { goBack } = useNavigateGoBack();

  const { member } = useMemberInfoState();

  const { quotation, applicationBody, spaceProductName } = (state ?? {}) as {
    quotation: any;
    applicationBody: ContractApplicationWrap;
    spaceProductName: string;
  };

  const findPrimaryImage = (quotation: any) => {
    let url = "";
    if (
      quotation.facility &&
      quotation.facility.mediaList &&
      Array.isArray(quotation.facility.mediaList) &&
      quotation.facility.mediaList.length > 0
    ) {
      const findPrimary = quotation.facility.mediaList.find((media: any) => media.isPrimary);
      if (findPrimary) {
        url = findPrimary.url;
      } else {
        url = quotation.facility.mediaList[0].url;
      }
    }
    return url;
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // task-list-item 클래스를 가진 요소들을 찾아서 클릭 이벤트 차단
    const taskItems = containerRef.current.querySelectorAll(".task-list-item");

    taskItems.forEach((item) => {
      // 클릭 이벤트 차단
      (item as HTMLElement).style.pointerEvents = "none";
    });
  }, []);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header
        headerType="CLOSE"
        // title="상세 내역"
        title={t("applicationForm.title")}
        onClickCloseButton={() => {
          goBack();
        }}
      />
      {state && (
        <div className="contract-page">
          <section className="contract-main ">
            <div className="gray-card">
              <div className="gray-card__product">
                <ResizedImage
                  className="minmax60"
                  isUseSmallSize
                  url={findPrimaryImage(quotation) || noImage}
                  alt={t("detail.commonSpaceImageAlt", { name: quotation?.facility?.facilityName })}
                />
                <div className="gray-card__contents">
                  <p className="gray-card__contents-title">{spaceProductName || "-"}</p>
                  <p className="gray-card__contents-address">{quotation?.facility?.facilityName!}</p>
                </div>
              </div>
              <div className="gray-card__info">
                <div className="base-section-layout-line">
                  <p className="base-section-layout-value text-primary3 pre-formatted text-left">
                    <>{formatDateTimeRange(String(quotation?.startDate!), String(quotation?.endDate!), "S2")}</>
                  </p>
                </div>
              </div>
            </div>
            <div className="gray-card__info">
              <>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.usagePeriod")}</p>
                  <p className="base-section-layout-value">
                    {quotation?.startDate
                      ? isEnglish
                        ? `${moment(quotation?.startDate).locale("en").format("MMMM D, YYYY")} (${moment(quotation?.endDate).locale("en").format("ddd")})`
                        : `${moment(quotation?.startDate).format(ViewYmdFormat.YYYY_MM_DD)}(${moment(quotation?.endDate).format("ddd")})`
                      : ""}
                  </p>
                </div>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.usageTime")}</p>
                  <p className="base-section-layout-value">
                    {`${quotation?.startDate && moment(quotation?.startDate).format("HH:mm")} ~
                      ${quotation?.endDate && moment(quotation?.endDate).format("HH:mm")}
                      ${getUseTimeDiff(quotation?.startDate, quotation?.endDate)}`}
                  </p>
                </div>
              </>
            </div>
          </section>

          <section className="base-section-layout top-divider">
            <div className="base-section-layout__container">
              <div className="base-section-layout__wrap">
                <div className="base-section-layout__title-wrap">
                  <h2>{t("applicationForm.applicantInformation")}</h2>
                </div>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.nameNickname")}</p>
                  <p className="base-section-layout-value">{member?.nickname || "-"}</p>
                </div>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.mobileNumber")}</p>
                  <p className="base-section-layout-value">{formatPhoneNumber(member?.phoneNumber || "")}</p>
                </div>
                <div className="base-section-layout-line">
                  <p className="base-section-layout-index text-primary3">{t("applicationForm.email")}</p>
                  <p className="base-section-layout-value">{member?.email}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="base-section-layout top-divider">
            <div className="base-section-layout__container">
              <div className="base-section-layout__wrap">
                <div className="base-section-layout__title-wrap pb10">
                  <h2>{t("applicationForm.additionalInformation")}</h2>
                </div>
                <div className="base-markdown">
                  {/* @ts-ignore*/}
                  <div
                    ref={containerRef}
                    // style={{ pointerEvents: "none" }}
                  >
                    {applicationBody?.formData?.richText ? <Viewer initialValue={applicationBody?.formData?.richText} /> : t("applicationForm.noInputInformation")}
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className="base-section-layout">
            <div className="base-section-layout__container pt0">
              <div className="base-section-layout__wrap">
                <div className="base-section-layout__title-wrap pb10">
                  <h2>{t("applicationForm.attachment")}</h2>
                </div>

                <ContractFileUpload fileList={applicationBody?.attachments || []} limit={10} isOnlyView={true} setFiles={(files) => {}} />
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default ContractApplicationDetail;
